/**
 * ADR-0231 — Agent Trust Engine slice 1: shared types (pure, no I/O).
 *
 * Vocabulary (docs/design/agent-trust-engine.md §3, §7):
 *   - Evidence tier — where a crawl report came from.
 *       edge_attested    — HMAC-signed report from a Cloudflare Worker
 *                          install: client IP, ASN and TLS facts were
 *                          observed by Cloudflare at the edge.
 *       install_attested — HMAC-signed report from a WordPress plugin
 *                          install: facts observed by the merchant's PHP
 *                          server (REMOTE_ADDR may be a proxy).
 *       anonymous        — unsigned (old installs) or a signature that did
 *                          not verify. Never yields `likely_impersonator`
 *                          or `suspicious`, never feeds learning.
 *     Attestation proves which install sent the report, NOT that its
 *     content is true (a merchant controls their own install).
 *   - Verdict label — verified | unverified | likely_impersonator.
 *   - Reason codes — CLOSED enum; every verdict carries ≥ 1.
 */
export const EVIDENCE_TIERS = ['edge_attested', 'install_attested', 'anonymous', 'sill_observed'];
/**
 * ADR-0234 — `sill_observed`: Sill itself received the request on one of
 * its own surfaces (agent card, MCP, A2A on edge.sill.so) and checked the
 * enrolled agent's signature there. No merchant install is in the path, so
 * nobody but the signer can fabricate it. It is trusted like an attested
 * report (it may verify and, for a cryptographic credential conflict,
 * accuse), but it carries no network / connection facts and never feeds
 * behaviour learning (`isLearningTier`).
 */
export function isAttestedTier(t) {
    return t === 'edge_attested' || t === 'install_attested' || t === 'sill_observed';
}
/** Tiers whose verified observations may train per-site profiles (ADR-0231 §4). */
export function isLearningTier(t) {
    return t === 'edge_attested' || t === 'install_attested';
}
export const VERDICT_LABELS = ['verified', 'unverified', 'likely_impersonator'];
/** Bumped on ANY rule change; the shadow job recomputes its window when it changes.
 *  v0.2 (ADR-0232): forward-confirmed reverse DNS, signature strength /
 *  replay / reuse / key pinning, only cryptographic failures accuse.
 *  v0.3 (ADR-0234): Sill agent credentials — enrolled-key signatures and
 *  live challenges are the top verified rung; a valid credential that
 *  claims another enrolled operator's token accuses.
 *  v0.4 (ADR-0236): learned context comes from stored per-site profiles;
 *  an open drift / verified-flag-rate alarm makes the engine abstain on
 *  learned behaviour and networks (`profile_drift`). */
export const TRUST_RULE_VERSION = 'trust-v0.4';
/** ADR-0232 — a Web Bot Auth validity window (expires − created) at or below this is "strong". */
export const SIG_STRONG_WINDOW_S = 300;
/**
 * ADR-0232 — forward-confirmed reverse DNS result for one request.
 *   confirmed  — a PTR name under a documented suffix resolves back to the address
 *   mismatch   — the PTR names are outside every documented suffix, or every
 *                in-suffix name definitively resolves elsewhere / not at all
 *   no_ptr     — the address has no PTR record (NXDOMAIN / empty answer)
 *   unknown    — resolver error, timeout, budget exhausted, queue full
 *   ineligible — the operator publishes rDNS but the address is not
 *                trustworthy (anonymous report, not Cloudflare-observed)
 * Only `confirmed` verifies; only `mismatch` is contradiction evidence,
 * and never alone.
 */
export const RDNS_RESULTS = ['confirmed', 'mismatch', 'no_ptr', 'unknown', 'ineligible'];
/** ADR-0232 — Web Bot Auth outcome as stored on the feature row. */
export const SIG_OUTCOMES = [
    'verified', 'invalid', 'expired', 'unknown_key', 'unsupported', 'malformed', 'directory_unavailable', 'replayed',
    // Review N5: window-policy violations (never accuse).
    'clock_skew', 'window_too_long',
    // Review N2: verified, but the nonce could not be checked (DB error). The
    // live path keeps it verified; the shadow engine does not verify on it.
    'nonce_unchecked',
];
/** ADR-0232 — pinned key-directory status of the key that signed. */
export const SIG_KEY_STATUSES = ['trusted', 'pending', 'unknown'];
export const TRUST_REASON_CODES = [
    // --- verified ---------------------------------------------------------
    /** Web Bot Auth signature verified, from a signer the claimed operator declares. */
    'sig_declared_signer',
    /** Source IP inside the operator's published ranges, from an attested report. */
    'published_range_attested',
    /** Forward-confirmed reverse DNS under a documented suffix, Cloudflare-observed address, attested (ADR-0232). */
    'rdns_confirmed',
    /** RESERVED placeholder from v0.1, never emitted (superseded by the two codes below; kept so stored rows stay valid). */
    'sill_credential',
    /** ADR-0234 — signed with a key the claimed agent's company enrolled with Sill (approved, active key). */
    'sill_credential_signature',
    /** ADR-0234 — passed Sill's live challenge (fresh single-use Sill nonce, signed with an enrolled key). Strongest rung. */
    'sill_credential_challenge',
    // --- likely_impersonator (attested sources only) -----------------------
    /** A signature claiming the operator's declared (pinned) signer failed verification. */
    'sig_failed_pinned_key',
    /** Verified signature from a signer the claimed operator does not use. */
    'signer_mismatch',
    /** IP outside the operator's complete + fresh published list AND outside its
     *  network (ASN) as observed at the Cloudflare edge. */
    'ip_outside_list_and_asn',
    /** UA carries a robots.txt-only token (Google-Extended, Applebot-Extended). */
    'robots_token_ua',
    /** Part of a name-rotation cluster (ADR-0229 sweep) by unregistered or failed claims. */
    'name_rotation',
    /** IP outside the operator's complete + fresh published list AND forward-confirmed
     *  reverse DNS contradicts the operator (Cloudflare-observed address, attested). ADR-0232. */
    'ip_outside_list_and_rdns',
    /** ADR-0234 — a VALID enrolled-key signature from one company, on a request whose
     *  user agent names a product token another enrolled company registered. */
    'credential_token_mismatch',
    // --- unverified (neutral; why nothing stronger was concluded) ---------
    /** UA claims no registered identity. */
    'no_registered_claim',
    /** Claimed operator publishes no signer and no ranges. */
    'no_verification_method',
    /** Proof was not available on this request (no signature, range check unknown). */
    'proof_unavailable',
    /** Deception-shaped evidence exists but the report is anonymous. */
    'deception_evidence_unattested',
    /** In published range, but the report is anonymous (IP is install-reported). */
    'range_match_unattested',
    /** In published range, attested, but the address is not trustworthy (proxied / unknown source). */
    'range_match_untrusted_ip',
    /** IP outside the list, but the network owner is unknown or inside the company's network. */
    'ip_outside_list_asn_inconclusive',
    /** IP outside the list, but not observed at the Cloudflare edge. */
    'ip_outside_list_not_edge_observed',
    // --- ADR-0232 (slice 2) ------------------------------------------------
    /** Reverse DNS contradicts the operator. Evidence only: never accuses alone;
     *  appended as corroboration when another deception rule fired. */
    'rdns_mismatch',
    /** Signature from a declared signer expired (could be a late replay or clock skew) — never accuses. */
    'sig_expired',
    /** Signature names a key not in the declared signer's directory (rotation race?) — never accuses. */
    'sig_unknown_key',
    /** Signature nonce already seen: a replay never verifies. */
    'sig_replayed',
    /** The nonce could not be checked (replay cache unavailable): the shadow engine does not verify on it. */
    'sig_nonce_unchecked',
    /** The same signature was seen on > 1 site or from too many networks: it no longer verifies. Never accuses. */
    'sig_reused',
    /** Signed with a key the bundled signer started serving less than the pin hold ago. */
    'sig_key_pending',
    /** Annotation on a signature verification: validity window longer than 5 minutes (weaker). */
    'sig_window_long',
    /** Annotation: the signature covered the User-Agent header (stronger). */
    'sig_ua_covered',
    /** Annotation: the signature carried no nonce (replays cannot be detected by nonce). */
    'sig_no_nonce',
    // --- ADR-0234 (slice 4) ------------------------------------------------
    /** Valid enrolled-key signature, but the user agent's name is not one that company
     *  registered (and no other enrolled company registered it). Evidence only. */
    'credential_token_unlisted',
    // --- suspicious flag (behavioural, needs ≥ 2 independent families) ----
    'profile_network_mismatch',
    'profile_connection_mismatch',
    'profile_header_mismatch',
    'profile_probe_behaviour',
    /** Too little verified history to compare behaviour (engine abstains). */
    'profile_insufficient',
    // --- ADR-0236 (slice 5) ------------------------------------------------
    /** A drift or verified-flag-rate alarm is open for this agent on this site: the
     *  engine abstains on learned behaviour and learned networks until re-baselined. */
    'profile_drift',
];
export function isTrustReasonCode(v) {
    return typeof v === 'string' && TRUST_REASON_CODES.includes(v);
}
/** Request path class (exact probe list — ADR-0231 §3). */
export const PATH_CLASSES = ['page', 'robots', 'discovery', 'probe'];
//# sourceMappingURL=types.js.map